
import { Observable } from 'rxjs';
import { map, filter, tap} from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { BaseService } from '@services/base.service';
import { IDecodedToken, Token } from '@state/reducers/auth/auth.models';
import { IAuthState } from '@state/reducers/auth/auth.reducer';
import { IState } from '@state/state.module';
import jwt_decode from 'jwt-decode';
import { LoggingService } from '../logging/logging.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable()
export class AuthService extends BaseService {

    constructor(
        private http: HttpClient,
        private store: Store<IState>,
        private logging: LoggingService,
        private oidcSecurityService: OidcSecurityService,
    ) {
        super();
        this.onInit();
    }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${AuthService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/auth';
    private state$: Observable<IAuthState>;

    public onInit() {
        this.state$ = this.store.select(x => x.auth);
        this.state$.pipe(
            filter(x => !!x?.principal),
            map(x => x.principal.token),
            tap(x => this.logging.log.info('AuthService: store token')))
            .subscribe(token => {
                if (token.decoded) {
                    this.storeToken(token);
                } else {
                    this.clearToken();
                }
            });
    }

    public clearToken() {
        sessionStorage.removeItem('auth_token');
    }

    public storeToken(token: Token) {
        if (!token.decoded) {
            throw Error('Fail to store token. Token can not be empty. Use clear token if your intent is to remove the token.');
        }
        this.logging.log.info('store token: old token expire: ' + new Date(this.retrieveToken()?.decoded.exp*1000));
        this.logging.log.info('store token: new token expire: ' + new Date(token.decoded.exp*1000));
        sessionStorage.setItem('auth_token', token.encoded);
    }

    public retrieveToken(): Token {
        const token = sessionStorage.getItem('auth_token');
        const isValidTokenString = new Token(token).decoded != null;
        return isValidTokenString ? new Token(token) : null;
    }

    // Check if userId exists
    public hasUserId() {
        // Change this a check if userId is null
        return true;
    }

    public getDecodedToken(token: string): IDecodedToken {
        return jwt_decode(token);
    }

    public requestNewAccessToken(tenantId: string): void {
        // we update localstorage
        // so that dashboard-users can retain the most
        // recently selected tenant between new browser windows
        localStorage.setItem('mruTenant', tenantId);

        // in addition we update the mru-tenant in sessionstorage
        // in order for oidc-client to use the new tenantId when exchanging the
        // code for the acces_token
        sessionStorage.setItem('mruTenant', tenantId);
        this.oidcSecurityService.logoffLocal('dashboard-ui');
        this.oidcSecurityService.authorize('dashboard-ui', { customParams: { acr_values: 'tenant:' + tenantId }});
    }
}
